import { motion } from "motion/react";
import {
    Calendar, Clock, User, ArrowLeft, Share2, Heart, MessageCircle,
    BookOpen, Tag, Eye, Twitter, Facebook, Linkedin,
    ChevronRight, Star, Quote
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ImageWithFallback } from "../../ui/imageWithFallback";

export function BlogDetailPage({ blogId, setCurrentPage, setSelectedBlogId }: any) {
    // This would typically come from an API or data store
    const blogPosts = [
        {
            id: 1,
            title: "The Future of AI in Software Development: Trends and Predictions for 2024",
            excerpt: "Explore how artificial intelligence is revolutionizing the software development landscape and what developers can expect in the coming year.",
            content: `
        <p class="text-lg leading-relaxed mb-6">Artificial Intelligence is fundamentally transforming the software development landscape, introducing new paradigms, tools, and methodologies that are reshaping how we build, test, and deploy applications. As we move through 2024, the integration of AI technologies into development workflows has accelerated at an unprecedented pace.</p>

        <h2 class="text-3xl font-semibold text-slate-900 mb-6 mt-10">The Current State of AI in Development</h2>
        
        <p class="mb-6">The adoption of AI-powered development tools has moved beyond experimental phases into mainstream production environments. From code generation assistants like GitHub Copilot to intelligent testing frameworks, AI is becoming an integral part of the developer toolkit.</p>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Key AI Technologies Reshaping Development</h3>
        
        <ul class="list-disc list-inside mb-6 space-y-2 pl-4">
          <li><strong>Code Generation:</strong> AI models can now generate contextually relevant code snippets, complete functions, and even entire modules based on natural language descriptions.</li>
          <li><strong>Automated Testing:</strong> Intelligent test case generation and execution, with AI identifying edge cases and potential failure points.</li>
          <li><strong>Bug Detection:</strong> Advanced static analysis tools powered by machine learning can identify potential security vulnerabilities and performance bottlenecks.</li>
          <li><strong>Documentation Generation:</strong> Automated creation of technical documentation, API documentation, and code comments.</li>
        </ul>

        <h2 class="text-3xl font-semibold text-slate-900 mb-6 mt-10">Impact on Developer Productivity</h2>
        
        <p class="mb-6">Research indicates that developers using AI-powered tools report a 40-60% increase in productivity, with significant improvements in code quality and reduced time spent on routine tasks. This shift allows developers to focus on higher-level architecture decisions and creative problem-solving.</p>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Real-World Applications</h3>
        
        <p class="mb-6">Companies across industries are implementing AI-driven development practices:</p>
        
        <ul class="list-disc list-inside mb-6 space-y-2 pl-4">
          <li><strong>Financial Services:</strong> AI-powered fraud detection systems and algorithmic trading platforms</li>
          <li><strong>Healthcare:</strong> Medical imaging analysis and patient data management systems</li>
          <li><strong>E-commerce:</strong> Personalized recommendation engines and inventory optimization</li>
          <li><strong>Manufacturing:</strong> Predictive maintenance and quality control systems</li>
        </ul>

        <h2 class="text-3xl font-semibold text-slate-900 mb-6 mt-10">Challenges and Considerations</h2>
        
        <p class="mb-6">While AI brings tremendous opportunities, it also introduces new challenges that development teams must navigate:</p>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Technical Challenges</h3>
        
        <ul class="list-disc list-inside mb-6 space-y-2 pl-4">
          <li><strong>Model Accuracy:</strong> Ensuring AI-generated code meets quality and security standards</li>
          <li><strong>Integration Complexity:</strong> Incorporating AI tools into existing development workflows</li>
          <li><strong>Performance Optimization:</strong> Managing computational resources for AI-powered applications</li>
          <li><strong>Data Privacy:</strong> Protecting sensitive information when using AI services</li>
        </ul>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Ethical and Social Implications</h3>
        
        <p class="mb-6">The rise of AI in software development raises important questions about job displacement, algorithmic bias, and the responsibility of developers to create ethical AI systems. Organizations must establish clear guidelines and governance frameworks for AI adoption.</p>

        <h2 class="text-3xl font-semibold text-slate-900 mb-6 mt-10">Looking Ahead: Predictions for 2024 and Beyond</h2>
        
        <p class="mb-6">As we look toward the future, several key trends are emerging:</p>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Emerging Trends</h3>
        
        <ul class="list-disc list-inside mb-6 space-y-2 pl-4">
          <li><strong>No-Code/Low-Code Evolution:</strong> AI-powered platforms will become more sophisticated, enabling non-developers to build complex applications</li>
          <li><strong>Intelligent DevOps:</strong> AI will optimize deployment strategies, resource allocation, and system monitoring</li>
          <li><strong>Personalized Development Environments:</strong> IDEs and development tools will adapt to individual developer preferences and patterns</li>
          <li><strong>Cross-Platform Intelligence:</strong> AI models will better understand and translate between different programming languages and frameworks</li>
        </ul>

        <h3 class="text-2xl font-semibold text-slate-900 mb-4 mt-8">Industry Transformation</h3>
        
        <p class="mb-6">The software development industry is undergoing a fundamental transformation. Traditional roles are evolving, with developers becoming more like architects and strategists, while AI handles routine coding tasks. This shift requires new skills and approaches to professional development.</p>

        <h2 class="text-3xl font-semibold text-slate-900 mb-6 mt-10">Conclusion</h2>
        
        <p class="mb-6">The future of AI in software development is both exciting and challenging. Organizations that embrace these technologies while addressing the associated risks will be best positioned to compete in the digital economy. The key to success lies in finding the right balance between AI automation and human creativity, ensuring that technology serves to enhance rather than replace human capabilities.</p>

        <p class="mb-6">As we continue to explore the possibilities of AI-powered development, one thing is clear: the landscape of software engineering will never be the same. The question is not whether AI will transform development, but how quickly organizations can adapt to this new reality and harness its full potential.</p>
      `,
            category: "ai",
            author: "Dr. Sarah Chen",
            authorRole: "Head of AI Research",
            authorBio: "Dr. Sarah Chen is a leading expert in artificial intelligence and machine learning with over 15 years of experience in both academia and industry. She holds a PhD in Computer Science from MIT and has published over 50 research papers on AI applications in software engineering.",
            authorAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b120?w=80&h=80&fit=crop&crop=face",
            publishedAt: "2024-01-15",
            updatedAt: "2024-01-16",
            readTime: "8 min read",
            views: 12500,
            likes: 347,
            image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop",
            tags: ["AI", "Machine Learning", "Software Development", "Future Tech", "Automation", "Programming"],
            featured: true,
            seoTitle: "The Future of AI in Software Development: 2024 Trends and Predictions",
            seoDescription: "Discover how artificial intelligence is revolutionizing software development in 2024. Learn about AI-powered coding tools, automation trends, and industry predictions.",
            metaKeywords: "AI software development, machine learning programming, AI coding tools, software automation, development trends 2024"
        }
        // Add more blog posts here...
    ];

    const relatedPosts = [
        {
            id: 2,
            title: "Building Scalable Microservices Architecture: Best Practices and Common Pitfalls",
            excerpt: "Learn the essential principles for designing and implementing microservices that can grow with your business needs.",
            image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=200&fit=crop",
            readTime: "12 min read",
            author: "Michael Rodriguez"
        },
        {
            id: 3,
            title: "Cybersecurity in the Age of Remote Work: Protecting Your Digital Assets",
            excerpt: "Discover essential cybersecurity strategies for organizations navigating the challenges of remote work environments.",
            image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=200&fit=crop",
            readTime: "6 min read",
            author: "Jennifer Park"
        },
        {
            id: 4,
            title: "The Rise of Low-Code/No-Code Platforms: Democratizing Software Development",
            excerpt: "Examine how low-code and no-code platforms are changing who can build software and what it means for the industry.",
            image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=200&fit=crop",
            readTime: "7 min read",
            author: "David Kim"
        }
    ];

    const currentPost:any = blogPosts[0];



    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleBackToBlog = () => {
        setSelectedBlogId(null);
    };

    const handleRelatedPostClick = (postId: number) => {
        setSelectedBlogId(postId);
        // Scroll to top when switching posts
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <article className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            {/* SEO Meta Tags would be handled here in a real application */}

            {/* Back Navigation */}
            <section className="px-6 py-8 max-w-7xl mx-auto">
                <Button
                    variant="ghost"
                    onClick={handleBackToBlog}
                    className="text-slate-600 hover:text-blue-600 mb-6"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Blog
                </Button>
            </section>

            {/* Hero Section */}
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Article Meta */}
                    <div className="flex flex-wrap items-center gap-4 mb-8">
                        <Badge className="bg-blue-600 text-white">
                            {currentPost.category.toUpperCase()}
                        </Badge>
                        {currentPost.featured && (
                            <Badge className="bg-yellow-500 text-white">
                                <Star className="w-3 h-3 mr-1" />
                                Featured
                            </Badge>
                        )}
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(currentPost.publishedAt)}
                        </div>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Clock className="w-4 h-4" />
                            {currentPost.readTime}
                        </div>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Eye className="w-4 h-4" />
                            {currentPost.views.toLocaleString()} views
                        </div>
                    </div>

                    {/* Article Title */}
                    <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                        {currentPost.title}
                    </h1>

                    {/* Article Excerpt */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl mb-10">
                        <Quote className="w-8 h-8 text-blue-500 mb-4" />
                        <p className="text-xl text-slate-700 leading-relaxed italic">
                            {currentPost.excerpt}
                        </p>
                    </div>

                    {/* Author Info */}
                    <div className="flex items-center justify-between mb-10 p-6 bg-white rounded-2xl shadow-lg border border-slate-200">
                        <div className="flex items-center gap-4">
                            <ImageWithFallback
                                src={currentPost.authorAvatar}
                                alt={currentPost.author}
                                className="w-16 h-16 rounded-full border-2 border-white shadow-lg"
                            />
                            <div>
                                <h3 className="text-xl font-semibold text-slate-900">{currentPost.author}</h3>
                                <p className="text-blue-600 font-medium">{currentPost.authorRole}</p>
                                <div className="flex items-center gap-2 text-slate-500 text-sm mt-1">
                                    <User className="w-3 h-3" />
                                    Published on {formatDate(currentPost.publishedAt)}
                                </div>
                            </div>
                        </div>

                        {/* Social Share Buttons */}
                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" className="flex items-center gap-2">
                                <Heart className="w-4 h-4" />
                                {currentPost.likes}
                            </Button>
                            <Button variant="outline" size="sm">
                                <Share2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* Featured Image */}
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="relative overflow-hidden rounded-2xl shadow-2xl"
                >
                    <ImageWithFallback
                        src={currentPost.image}
                        alt={currentPost.title}
                        className="w-full h-[500px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </motion.div>
            </section>

            {/* Article Content */}
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12"
                >
                    {/* Table of Contents */}
                    <div className="bg-slate-50 rounded-xl p-6 mb-10">
                        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <BookOpen className="w-5 h-5" />
                            Table of Contents
                        </h2>
                        <nav className="space-y-2">
                            <a href="#current-state" className="block text-blue-600 hover:text-blue-800 transition-colors">
                                1. The Current State of AI in Development
                            </a>
                            <a href="#productivity-impact" className="block text-blue-600 hover:text-blue-800 transition-colors">
                                2. Impact on Developer Productivity
                            </a>
                            <a href="#challenges" className="block text-blue-600 hover:text-blue-800 transition-colors">
                                3. Challenges and Considerations
                            </a>
                            <a href="#future-predictions" className="block text-blue-600 hover:text-blue-800 transition-colors">
                                4. Looking Ahead: Predictions for 2024 and Beyond
                            </a>
                        </nav>
                    </div>

                    {/* Main Content */}
                    <div
                        className="prose prose-slate prose-lg max-w-none"
                        dangerouslySetInnerHTML={{ __html: currentPost.content }}
                    />

                    {/* Article Tags */}
                    <div className="mt-12 pt-8 border-t border-slate-200">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Tag className="w-5 h-5" />
                            Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {currentPost.tags.map((tag:any) => (
                                <Button
                                    key={tag}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                                >
                                    #{tag}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Social Sharing */}
                    <div className="mt-8 pt-8 border-t border-slate-200">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">Share This Article</h3>
                        <div className="flex gap-3">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                <Twitter className="w-4 h-4 mr-2" />
                                Twitter
                            </Button>
                            <Button className="bg-blue-800 hover:bg-blue-900 text-white">
                                <Facebook className="w-4 h-4 mr-2" />
                                Facebook
                            </Button>
                            <Button className="bg-blue-700 hover:bg-blue-800 text-white">
                                <Linkedin className="w-4 h-4 mr-2" />
                                LinkedIn
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* Author Biography */}
            <section className="px-6 pb-16 max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-8 border border-slate-200"
                >
                    <h2 className="text-2xl font-semibold text-slate-900 mb-6">About the Author</h2>
                    <div className="flex flex-col md:flex-row gap-6">
                        <ImageWithFallback
                            src={currentPost.authorAvatar}
                            alt={currentPost.author}
                            className="w-24 h-24 rounded-full border-4 border-white shadow-lg"
                        />
                        <div className="flex-1">
                            <h3 className="text-xl font-semibold text-slate-900 mb-2">{currentPost.author}</h3>
                            <p className="text-blue-600 font-medium mb-4">{currentPost.authorRole}</p>
                            <p className="text-slate-600 leading-relaxed mb-4">{currentPost.authorBio}</p>
                            <div className="flex gap-3">
                                <Button variant="outline" size="sm">
                                    <User className="w-4 h-4 mr-2" />
                                    View Profile
                                </Button>
                                <Button variant="outline" size="sm">
                                    Follow
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* Related Articles */}
            <section className="px-6 pb-24 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold text-slate-900 mb-8">Related Articles</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {relatedPosts.map((post, index) => (
                            <motion.article
                                key={post.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                                onClick={() => handleRelatedPostClick(post.id)}
                            >
                                <div className="relative overflow-hidden h-48">
                                    <ImageWithFallback
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-slate-900 mb-3 hover:text-blue-600 transition-colors line-clamp-2">
                                        {post.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                                        {post.excerpt}
                                    </p>
                                    <div className="flex items-center justify-between text-sm text-slate-500">
                                        <span>{post.author}</span>
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {post.readTime}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        className="text-blue-600 hover:text-blue-700 p-0 h-auto font-medium mt-4 group"
                                    >
                                        Read More
                                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* Newsletter CTA */}
            <section className="px-6 pb-24 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-12 text-center text-white"
                >
                    <h2 className="text-3xl font-bold mb-4">
                        Stay Updated with Tech Insights
                    </h2>
                    <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
                        Subscribe to our newsletter and get the latest insights, tutorials, and industry news
                        delivered straight to your inbox every week.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/60 backdrop-blur-sm"
                        />
                        <Button className="bg-white text-blue-600 hover:bg-blue-50 px-6">
                            Subscribe
                        </Button>
                    </div>
                </motion.div>
            </section>
        </article>
    );
}